import NavigationBackHeader from '@app/components/presentational/common/NavigationBackHeader/NavigationBackHeader';
import useIsOverflowing from '@app/hooks/useIsOverflowing';
import {
  Box,
  Button,
  IconButton,
  Icons,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import React, { MouseEvent, useCallback, useRef, useState } from 'react';
import { createStyles } from './SubmissionsSidebar.styles';
import ManageExtension from '../ManageExtension/ManageExtension';
import { ExtensionUser } from '../ManageExtension/ManageExtension.types';

type SubmissionAssignment = {
  description?: string;
  endDate?: string;
  gradesReleasedAt?: string;
  gradeType?: string;
  groupSet?: string;
  id: string;
  isHidden?: boolean;
  isAnonymised?: boolean;
  objectId?: string;
  parentAssignmentId?: string;
  provider?: string;
  shouldCloseOnDueDate?: boolean;
  startDate?: string;
  studentsCount?: number;
  students?: string[];
  submissions?: string[];
  title: string;
};

type Props = {
  accessToken?: string;
  assignment?: SubmissionAssignment;
  assignmentCreationError?: any;
  assignmentUpdateError?: any;
  backRoute: string;
  canCreateExtensions: boolean;
  canEditExtensions: boolean;
  createExtension: (params: any) => Promise<SubmissionAssignment | null>;
  editExtension: (params: any) => Promise<SubmissionAssignment | null>;
  extensions: SubmissionAssignment[];
  loadStudents: (spaceId: string) => Promise<any[]>;
  onExtensionSaved: (savedExtension?: SubmissionAssignment) => Promise<void>;
  resetExtensionErrors: () => void;
  selectedExtensionId: string;
  spaceId: string;
  user: ExtensionUser | null;
};

const SubmissionSidebar = ({
  accessToken,
  assignment,
  assignmentCreationError,
  assignmentUpdateError,
  backRoute,
  canCreateExtensions,
  canEditExtensions,
  createExtension,
  editExtension,
  extensions,
  loadStudents,
  onExtensionSaved,
  resetExtensionErrors,
  selectedExtensionId,
  spaceId,
  user,
}: Props) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const isDark = theme.palette.mode === 'dark';
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const assignmentTitle = assignment?.title || 'Submissions';
  const [titleOverflowRef, isTitleWidthOverflowing] =
    useIsOverflowing<HTMLHeadingElement>();
  const [isTitleHeightOverflowing, setIsTitleHeightOverflowing] =
    useState(false);
  const titleResizeObserver = useRef<ResizeObserver | null>(null);
  const setTitleRef = useCallback(
    (node: HTMLHeadingElement | null) => {
      titleOverflowRef(node);
      if (titleResizeObserver.current) {
        titleResizeObserver.current.disconnect();
      }
      titleResizeObserver.current = null;
      if (!node) return;

      const checkHeightOverflow = () =>
        setIsTitleHeightOverflowing(node.scrollHeight > node.clientHeight);
      checkHeightOverflow();
      titleResizeObserver.current = new ResizeObserver(checkHeightOverflow);
      titleResizeObserver.current.observe(node);
    },
    [titleOverflowRef]
  );
  const isTitleOverflowing =
    isTitleWidthOverflowing || isTitleHeightOverflowing;
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedExtension, setSelectedExtension] =
    useState<SubmissionAssignment>();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [menuExtension, setMenuExtension] = useState<SubmissionAssignment>();

  const closeExtension = () => {
    setIsOpen(false);
    setSelectedExtension(undefined);
  };

  const openMenu = (
    event: MouseEvent<HTMLButtonElement>,
    extension: SubmissionAssignment
  ) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuExtension(extension);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuExtension(undefined);
  };

  const openEditExtension = () => {
    if (!menuExtension || !canEditExtensions) return;
    setSelectedExtension(menuExtension);
    setIsOpen(true);
    closeMenu();
  };

  return (
    <>
      <Box sx={styles.sidebarWrapper}>
        <Box sx={styles.sidebarNavigation}>
          <NavigationBackHeader route={backRoute} />
        </Box>
        <Box sx={styles.sidebarHeader}>
          <Tooltip
            title={assignmentTitle}
            disableFocusListener={!isMdUp || !isTitleOverflowing}
            disableHoverListener={!isMdUp || !isTitleOverflowing}
            disableTouchListener
          >
            <Box sx={styles.assignmentTitle} ref={setTitleRef}>
              {assignmentTitle}
            </Box>
          </Tooltip>
          {canCreateExtensions && (
            <Button
              type="button"
              onClick={() => {
                setSelectedExtension(undefined);
                setIsOpen(true);
              }}
              sx={styles.newExtension}
            >
              <Icons.Add />
              New extension
            </Button>
          )}
        </Box>
        <Box component="ul" sx={styles.sidebarContent}>
          <Box sx={styles.extensions}>
            {extensions.map((extension) => {
              const isExtension = Boolean(extension.parentAssignmentId);
              return (
                <Box
                  component="li"
                  key={extension.id}
                  aria-current={
                    selectedExtensionId === extension.id ? 'true' : undefined
                  }
                  sx={styles.extensionItem}
                >
                  {isExtension && (
                    <Icons.ExtensionOutlined
                      fontSize="medium"
                      sx={styles.extensionIcon}
                      aria-hidden
                    />
                  )}
                  <Typography sx={styles.extensionTitle}>
                    {isExtension ? extension.title : 'Main cohort'} (
                    {extension.submissions?.length || 0}/
                    {extension.studentsCount || 0})
                  </Typography>
                  {isExtension && canEditExtensions && (
                    <Tooltip title="Extension options">
                      <Box component="span" sx={styles.menuButtonWrapper}>
                        <IconButton
                          id={`extension-options-${extension.id}`}
                          type="button"
                          aria-label={`Options for ${extension.title}`}
                          aria-controls={
                            menuExtension?.id === extension.id
                              ? 'extension-options-menu'
                              : undefined
                          }
                          aria-expanded={
                            menuExtension?.id === extension.id || undefined
                          }
                          aria-haspopup="menu"
                          sx={styles.menuIcon}
                          onClick={(event) => openMenu(event, extension)}
                        >
                          <Icons.MoreVert />
                        </IconButton>
                      </Box>
                    </Tooltip>
                  )}
                  <IconButton
                    type="button"
                    aria-label={`${extension.isHidden ? 'Unhide' : 'Hide'} ${
                      isExtension ? extension.title : 'Main cohort'
                    }`}
                    sx={styles.visibilityIcon}
                    onClick={() =>
                      editExtension({
                        accessToken,
                        assignment: {
                          ...extension,
                          isHidden: !extension.isHidden,
                        },
                        spaceId,
                      })
                    }
                  >
                    {extension.isHidden ? (
                      <Icons.VisibilityOffOutlined />
                    ) : (
                      <Icons.VisibilityOutlined />
                    )}
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        </Box>
        <Menu
          id="extension-options-menu"
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={closeMenu}
          aria-labelledby={
            menuExtension ? `extension-options-${menuExtension.id}` : undefined
          }
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            style: {
              backgroundColor: isDark ? '#363636' : undefined,
              color: isDark ? '#ffffff' : undefined,
              marginTop: '4px',
              width: 'max-content',
            },
          }}
          sx={styles.extensionMenu}
        >
          <MenuItem
            icon={<Icons.EditOutlined fontSize="small" />}
            label="Edit"
            onClick={openEditExtension}
          />
          <MenuItem
            icon={<Icons.DeleteOutlined fontSize="small" />}
            label="Delete"
          />
        </Menu>
      </Box>
      {assignment && user && (canCreateExtensions || canEditExtensions) && (
        <ManageExtension
          accessToken={accessToken}
          assignment={assignment}
          assignmentCreationError={assignmentCreationError}
          assignmentUpdateError={assignmentUpdateError}
          createExtension={createExtension}
          editExtension={editExtension}
          extension={selectedExtension}
          isOpen={isOpen}
          loadStudents={loadStudents}
          onExtensionSaved={onExtensionSaved}
          onCloseModal={closeExtension}
          resetExtensionErrors={resetExtensionErrors}
          spaceId={spaceId}
          user={user}
        />
      )}
    </>
  );
};

export default SubmissionSidebar;
